import {
  createContext,
  type Dispatch,
  type FC,
  type ReactNode,
  type SetStateAction,
  useCallback,
  useContext,
  useState,
} from 'react'
import { usePathname } from '@/hooks/use-pathname'
import { usePersistedSideNavExpanded } from '@/hooks/use-persisted-side-nav-expanded'
import {
  isDocumentsDeclarationPath,
  isDocumentsEditorSidenavPath,
} from '@/routes/documents-editor-paths'

export type SideNavExpandedContextValue = {
  sideNavExpanded: boolean
  setSideNavExpanded: Dispatch<SetStateAction<boolean>>
}

const SideNavExpandedContext = createContext<SideNavExpandedContextValue | null>(
  null
)

export const SideNavExpandedProvider: FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [preferred, setPreferred] = usePersistedSideNavExpanded()
  const pathname = usePathname()
  // Une déclaration s'ouvre sidenav repliée ; la déplier ne vaut que pour celle-ci (même chemin), sans
  // toucher à la préférence. Les autres écrans de saisie la replient sans toggle. En sortant, on
  // retrouve la sidenav telle que l'utilisateur l'avait laissée.
  const [declarationOverride, setDeclarationOverride] = useState<{
    pathname: string
    expanded: boolean
  } | null>(null)
  const onDeclaration = isDocumentsDeclarationPath(pathname)
  const sideNavExpanded = onDeclaration
    ? declarationOverride?.pathname === pathname && declarationOverride.expanded
    : preferred && !isDocumentsEditorSidenavPath(pathname)
  const setSideNavExpanded: Dispatch<SetStateAction<boolean>> = useCallback(
    (value) => {
      if (!onDeclaration) {
        setPreferred(value)
        return
      }
      setDeclarationOverride((current) => {
        const was = current?.pathname === pathname && current.expanded
        return {
          pathname,
          expanded: typeof value === 'function' ? value(was) : value,
        }
      })
    },
    [onDeclaration, pathname, setPreferred]
  )
  return (
    <SideNavExpandedContext.Provider
      value={{ sideNavExpanded, setSideNavExpanded }}
    >
      {children}
    </SideNavExpandedContext.Provider>
  )
}

export const useSideNavExpandedLayout = (): SideNavExpandedContextValue => {
  const ctx = useContext(SideNavExpandedContext)
  if (!ctx) {
    throw new Error(
      'useSideNavExpandedLayout must be used within SideNavExpandedProvider'
    )
  }
  return ctx
}
